// ─── ProfilePage.jsx ──────────────────────────────────────────────────────────
// Glow Bingo — Profile screen
// White wireframe → converted to dark system matching GlowBingo / WalletPage
// No Tailwind. All tokens from theme.js.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import {
  Home,
  Gamepad2,
  Gift,
  User,
  Trophy,
  DollarSign,
  Users,
  Headphones,
  ShieldCheck,
  ChevronRight,
  Bell,
  Edit3,
  MapPin,
  Settings,
  VolumeX,
  Volume2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { theme, injectTheme } from "../assets/theme";

// ─── Static data ──────────────────────────────────────────────────────────────

const SUPPORT_ITEMS = [
  {
    id: "contact",
    Icon: Headphones,
    label: "Contact Support",
    sub: "We are here to help 24/7",
    accent: true,
  },
];
function AvatarHero({ user }) {
  const displayName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(" ");
  const handle = user.username ? `@${user.username}` : `@player${user.id}`;
  const phone = user.phone_number || "No phone linked yet";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "32px 24px 20px",
        position: "relative",
      }}
    >
      {/* Soft radial glow behind avatar */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "160px",
          height: "160px",
          borderRadius: theme.radius.full,
          background: "rgba(249,115,22,0.10)",
          filter: "blur(36px)",
          pointerEvents: "none",
        }}
      />

      {/* Avatar ring */}
      <div style={{ position: "relative", zIndex: 1, marginBottom: "18px" }}>
        {/* Gradient border ring */}
        <div
          style={{
            width: "92px",
            height: "92px",
            borderRadius: theme.radius.full,
            padding: "3px",
            background: `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`,
            boxShadow: `0 0 30px ${theme.color.accentGlow}`,
          }}
        >
          {/* Inner avatar area */}
          <div
            style={{
              width: "100%",
              height: "100%",
              borderRadius: theme.radius.full,
              background: "linear-gradient(145deg, #1e1a12, #2c2214)",
              border: `2px solid ${theme.color.bgSurface}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {/* SVG silhouette placeholder */}
            <svg
              width="56"
              height="56"
              viewBox="0 0 56 56"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Head */}
              <circle cx="28" cy="20" r="12" fill="rgba(251,146,60,0.45)" />
              {/* Body */}
              <ellipse
                cx="28"
                cy="48"
                rx="19"
                ry="14"
                fill="rgba(251,146,60,0.30)"
              />
            </svg>
          </div>
        </div>

        {/* Online dot */}
        {/* <span
          style={{
            position: "absolute",
            bottom: "5px",
            right: "5px",
            width: "14px",
            height: "14px",
            borderRadius: theme.radius.full,
            background: "#22c55e",
            border: `2.5px solid ${theme.color.bgSurface}`,
            boxShadow: "0 0 8px rgba(34,197,94,0.65)",
          }}
        /> */}

        {/* Edit pencil */}
        {/* <button
          aria-label="Edit profile photo"
          style={{
            position: "absolute",
            top: "2px",
            right: "-4px",
            width: "24px",
            height: "24px",
            borderRadius: theme.radius.full,
            background: theme.color.accentLight,
            border: `2px solid ${theme.color.bgSurface}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: theme.shadow.logo,
            transition: theme.transition.base,
          }}
        >
          <Edit3 size={11} color="#fff" />
        </button> */}
      </div>

      {/* Username */}
      <p
        style={{
          fontFamily: theme.font.family,
          fontWeight: theme.font.weightBlack,
          fontSize: "22px",
          color: theme.color.textPrimary,
          letterSpacing: "0.01em",
          marginBottom: "5px",
          zIndex: 1,
        }}
      >
        {handle}
      </p>

      {/* Display name */}
      <p
        style={{
          fontFamily: theme.font.bodyFamily,
          fontWeight: theme.font.weightSemi,
          fontSize: "14px",
          color: theme.color.textMuted,
          marginBottom: "3px",
          zIndex: 1,
        }}
      >
        {displayName}
      </p>

      {/* Phone */}
      <p
        style={{
          fontFamily: theme.font.bodyFamily,
          fontWeight: theme.font.weightReg,
          fontSize: "12px",
          color: theme.color.textDim,
          zIndex: 1,
        }}
      >
        {phone}
      </p>
    </div>
  );
}

// ─── Divider ─────────────────────────────────────────────────────────────────

function Divider() {
  return (
    <div
      style={{
        margin: "0 20px",
        height: "1px",
        background: `linear-gradient(to right, transparent, ${theme.color.borderDivide}, transparent)`,
      }}
    />
  );
}

// ─── SectionLabel ─────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "10px",
      }}
    >
      <span
        style={{
          width: "4px",
          height: "16px",
          borderRadius: theme.radius.full,
          background: theme.color.accent,
          boxShadow: theme.shadow.glowBar,
          flexShrink: 0,
        }}
      />
      <h2
        style={{
          fontFamily: theme.font.family,
          fontWeight: theme.font.weightBlack,
          fontSize: "12px",
          color: theme.color.textDim,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        {children}
      </h2>
    </div>
  );
}

// ─── PerformanceBar ───────────────────────────────────────────────────────────

function PerformanceBar({ stats }) {
  const PERFORMANCE = [
    { Icon: Trophy, value: String(stats.gamesWon), label: "Games Won" },
    {
      Icon: DollarSign,
      value: `${stats.earnings.toLocaleString()} ETB`,
      label: "Earnings",
    },
    { Icon: Users, value: String(stats.invites), label: "Invites" },
  ];
  return (
    <section style={{ padding: "0 16px" }}>
      <SectionLabel>Performance</SectionLabel>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          borderRadius: theme.radius.xl,
          background: "rgba(255,255,255,0.03)",
          border: `1px solid ${theme.color.borderCard}`,
          overflow: "hidden",
        }}
      >
        {PERFORMANCE.map((stat, i) => {
          const { Icon } = stat;
          return (
            <div key={stat.label} style={{ display: "contents" }}>
              {/* Stat cell */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                  padding: "20px 8px",
                }}
              >
                <Icon
                  size={16}
                  color={theme.color.accentLight}
                  style={{ opacity: 0.8 }}
                />
                <span
                  style={{
                    fontFamily: theme.font.family,
                    fontWeight: theme.font.weightBlack,
                    fontSize: "19px",
                    color: theme.color.textPrimary,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </span>
                <span
                  style={{
                    fontSize: "9px",
                    fontFamily: theme.font.bodyFamily,
                    fontWeight: theme.font.weightSemi,
                    color: theme.color.textDim,
                    textTransform: "uppercase",
                    letterSpacing: "0.13em",
                    textAlign: "center",
                  }}
                >
                  {stat.label}
                </span>
              </div>

              {/* Vertical divider between cells */}
              {i < PERFORMANCE.length - 1 && (
                <span
                  style={{
                    width: "1px",
                    height: "46px",
                    background: theme.color.borderDivide,
                    flexShrink: 0,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MuteSwitch({ on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      aria-label={on ? "Unmute game sound" : "Mute game sound"}
      style={{
        width: "46px",
        height: "25px",
        borderRadius: "9999px",
        background: on
          ? `linear-gradient(90deg, ${theme.color.playFrom}, ${theme.color.playTo})`
          : "rgba(255,255,255,0.12)",
        border: "none",
        cursor: "pointer",
        position: "relative",
        transition: "background 0.25s ease",
        boxShadow: on ? `0 0 12px ${theme.color.accentGlow}` : "none",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "4px",
          left: on ? "23px" : "4px",
          width: "17px",
          height: "17px",
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.22s ease",
          boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
        }}
      />
    </button>
  );
}
// ─── SupportItem ──────────────────────────────────────────────────────────────

function SupportItem({ item, delay }) {
  const [hovered, setHovered] = useState(false);
  const { Icon } = item;

  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={item.label}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "14px 16px",
        borderRadius: theme.radius.lg,

        // Accent card (Contact Support) gets the orange gradient
        background: item.accent
          ? `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`
          : hovered
            ? theme.color.bgCardHover
            : theme.color.bgCard,

        border: item.accent
          ? "none"
          : `1px solid ${hovered ? theme.color.borderHover : theme.color.borderCard}`,

        boxShadow: item.accent ? "0 6px 28px rgba(249,115,22,0.32)" : "none",

        cursor: "pointer",
        transition: theme.transition.base,
        textAlign: "left",
        transform: hovered && !item.accent ? "translateX(2px)" : "none",
        animation: "fadeInUp 0.35s ease both",
        animationDelay: `${delay}s`,
      }}
    >
      {/* Icon circle */}
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: theme.radius.md,
          background: item.accent
            ? "rgba(255,255,255,0.20)"
            : "rgba(255,255,255,0.05)",
          border: item.accent ? "none" : `1px solid ${theme.color.borderCard}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={18} color={item.accent ? "#fff" : theme.color.textMuted} />
      </div>

      {/* Label + subtitle */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontFamily: theme.font.bodyFamily,
            fontWeight: theme.font.weightBold,
            fontSize: "13px",
            color: item.accent ? "#ffffff" : theme.color.textSub,
            marginBottom: "3px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.label}
        </p>
        <p
          style={{
            fontFamily: theme.font.bodyFamily,
            fontWeight: theme.font.weightReg,
            fontSize: "11px",
            color: item.accent ? "rgba(255,255,255,0.68)" : theme.color.textDim,
          }}
        >
          {item.sub}
        </p>
      </div>

      {/* Trailing: orange dot badge or chevron */}

      <ChevronRight
        size={16}
        color={item.accent ? "rgba(255,255,255,0.65)" : theme.color.textDim}
        style={{
          flexShrink: 0,
          opacity: hovered ? 1 : 0.55,
          transition: theme.transition.base,
        }}
      />

      {item.radio && <input type="radio" name="support" value={item.id} />}
    </button>
  );
}

// ─── ProfilePage (Main) ───────────────────────────────────────────────────────
export default function ProfileComp() {
  const { user, soundMuted, toggleSound, refreshUser } = useAuth();
  useEffect(() => {
    injectTheme();
    refreshUser();
  }, []);

  if (!user) return null; // AuthContext is still loading

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        background: theme.color.bgSurface,
        overflow: "auto",
        boxShadow: theme.shadow.phone,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      {/* ── Scrollable body ─────────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          scrollbarWidth: "none",
          paddingBottom: "100px",
        }}
      >
        {/* Avatar + user info */}
        <AvatarHero user={user} />

        <Divider />

        {/* Performance stats */}
        <PerformanceBar stats={user.stats} />

        <Divider />

        {/* Support & Feedback */}
        <section style={{ padding: "0 16px" }}>
          <SectionLabel>Support &amp; Feedback</SectionLabel>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {SUPPORT_ITEMS.map((item, i) => (
              <SupportItem key={item.id} item={item} delay={i * 0.06} />
            ))}

            {/* Mute row — real switch, real persistence */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                borderRadius: theme.radius.lg,
                background: theme.color.bgCard,
                border: `1px solid ${theme.color.borderCard}`,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "14px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: theme.radius.md,
                    background: "rgba(255,255,255,0.05)",
                    border: `1px solid ${theme.color.borderCard}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {soundMuted ? (
                    <VolumeX size={18} color={theme.color.textMuted} />
                  ) : (
                    <Volume2 size={18} color={theme.color.accentLight} />
                  )}{" "}
                </div>
                <div>
                  <p
                    style={{
                      fontFamily: theme.font.bodyFamily,
                      fontWeight: theme.font.weightBold,
                      fontSize: "13px",
                      color: theme.color.textSub,
                      marginBottom: "3px",
                    }}
                  >
                    Sound
                  </p>
                  <p
                    style={{
                      fontFamily: theme.font.bodyFamily,
                      fontSize: "11px",
                      color: theme.color.textDim,
                    }}
                  >
                    {soundMuted
                      ? "Sound is off"
                      : "Play sounds during gameplay"}
                  </p>
                </div>
              </div>
              <MuteSwitch on={!soundMuted} onToggle={toggleSound} />
            </div>
          </div>
        </section>

        {/* Help center hint */}
        <p
          style={{
            fontSize: "11px",
            fontFamily: theme.font.bodyFamily,
            fontWeight: theme.font.weightReg,
            color: theme.color.textDim,
            textAlign: "center",
            padding: "0 36px",
            lineHeight: 1.55,
            marginTop: "-10px",
          }}
        >
          Check our Help Center for frequently asked questions and guides.
        </p>

        <div style={{ height: "18px" }} />
      </div>
    </div>
  );
}
